/**
 * Shared hook to resolve the current organization's internal UUID.
 *
 * Prefers the backend-authoritative active organization mapping because
 * Clerk publicMetadata can be stale after resets or reprovisioning.
 * Falls back to Clerk metadata and finally Clerk org ID for endpoints
 * that accept auth organization identifiers.
 */

import { useOrganization } from '@clerk/clerk-react';

import { useOrganizations } from '@/hooks/use-organizations';

export function useOrganizationId(): string | undefined {
  const { organization } = useOrganization();
  const { organizations, isLoading } = useOrganizations();

  const activeOrganization = organizations.find((org) => org.isActive);
  if (activeOrganization?.id) {
    return activeOrganization.id;
  }

  // While the backend organizations query is still loading, return undefined
  // to prevent firing API calls with stale Clerk publicMetadata IDs.
  if (isLoading) {
    return undefined;
  }

  const internalId = (organization?.publicMetadata as { internalId?: string } | null)?.internalId;

  return internalId ?? organization?.id;
}
